<template>
  <div class="card mb-25 border-0 rounded-0 bg-white website-visitors radius-7 billing-card">
    <div class="card-body">
      <div class="billing-card__header">
        <h6 class="card-title fw-bold mb-0">Meta de faturamento</h6>
      </div>

      <div class="billing-card__summary">
        <div class="billing-card__summary-item billing-card__summary-item--success">
          <span>Entradas</span>
          <strong>{{ formatCurrency(getMetricsMonthBillingGraphic.invoicing) }}</strong>
          <small class="billing-card__scope-note">Somente usuários permitidos.</small>
        </div>

        <div class="billing-card__summary-item billing-card__summary-item--goal">
          <span>Meta da empresa</span>
          <strong>{{ formatCurrency(getMetricsMonthBillingGraphic.goal) }}</strong>
        </div>
      </div>

      <div id="websiteVisitorsChart" class="chart" v-if="getMetricsMonthBillingGraphic">
        <apexchart
          key="billing-chart"
          type="bar"
          :height="chartHeight"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useStore } from "vuex";

export default defineComponent({
  name: "MetricsMonthBillingGraphic",
  setup() {
    const store = useStore();

    const formatCurrency = (value: number | string) =>
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
      }).format(Number(value) || 0);

    const getMetricsMonthBillingGraphic = computed(
      () => store.getters.getMetricsMonthBillingGraphic || {}
    );

    const baseChartOptions = {
      chart: {
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          endingShape: "rounded",
        },
      },
      dataLabels: {
        enabled: false,
      },
      colors: ["#06B48A", "#836FFF"],
      stroke: {
        show: false,
      },
      fill: {
        opacity: 1,
      },
      tooltip: {
        style: {
          fontSize: "14px",
          fontFamily: "Red Hat Display, sans-serif",
        },
      },
      grid: {
        show: true,
        strokeDashArray: 5,
        borderColor: "#d9e9ef",
      },
    };

    const chartHeight = computed(() => 199);

    const chartSeries = computed(() => [
      {
        name: "Valor",
        data: [
          {
            x: "Entradas",
            y: getMetricsMonthBillingGraphic.value.invoicing ?? 0,
            fillColor: "#06B48A",
          },
          {
            x: "Meta",
            y: getMetricsMonthBillingGraphic.value.goal ?? 0,
            fillColor: "#836FFF",
          },
        ],
      },
    ]);

    const chartOptions = computed(() => {
      const compactLabel = (value: number) => {
        const absValue = Math.abs(value);

        if (absValue >= 1000000) {
          return `R$ ${(value / 1000000).toFixed(1)} mi`;
        }

        if (absValue >= 1000) {
          return `R$ ${(value / 1000).toFixed(0)} mil`;
        }

        return `R$ ${value.toFixed(0)}`;
      };

      return {
        ...baseChartOptions,
        plotOptions: {
          bar: {
            ...baseChartOptions.plotOptions.bar,
            horizontal: true,
            columnWidth: "50%",
            barHeight: "45%",
          },
        },
        xaxis: {
          labels: {
            show: true,
            formatter: (val: number) => compactLabel(Number(val)),
            style: {
              fontFamily: "Red Hat Display, sans-serif",
              colors: "#2c3e50",
              fontSize: "11px",
              fontWeight: 500,
            },
          },
        },
        yaxis: {
          categories: ["Entradas", "Meta"],
          labels: {
            style: {
              fontFamily: "Red Hat Display, sans-serif",
              colors: ["#06B48A", "#836FFF"],
              fontSize: "12px",
              fontWeight: 700,
            },
          },
        },
        tooltip: {
          ...baseChartOptions.tooltip,
          y: {
            formatter: (val: number) => formatCurrency(val),
          },
        },
      };
    });

    return {
      chartHeight,
      chartOptions,
      chartSeries,
      formatCurrency,
      getMetricsMonthBillingGraphic,
    };
  },
});
</script>

<style scoped>
.billing-card .card-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.billing-card__header {
  margin-bottom: 10px;
}

.billing-card__summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.billing-card .chart {
  display: flex;
  align-items: flex-start;
  padding-bottom: 6px;
}

.billing-card .chart :deep(.vue-apexcharts) {
  width: 100%;
}

.billing-card__summary-item {
  border-radius: 7px;
  padding: 12px;
  color: #2b2a3f;
}

.billing-card__summary-item span {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.billing-card__summary-item strong {
  display: block;
  font-size: 18px;
  line-height: 1.2;
}

.billing-card__scope-note {
  display: block;
  margin-top: 6px;
  font-size: 10px;
  font-weight: 400;
  line-height: 1.3;
  opacity: 0.8;
}

.billing-card__summary-item--success {
  background: rgba(6, 180, 138, 0.12);
}

.billing-card__summary-item--goal {
  background: rgba(131, 111, 255, 0.12);
}

@media (max-width: 767px) {
  .billing-card .card-body {
    padding: 16px 14px;
    display: block;
  }

  .billing-card .chart {
    display: block;
    padding-bottom: 0;
  }

  .billing-card__summary-item {
    padding: 10px;
    border-radius: 7px;
  }

  .billing-card__summary-item strong {
    font-size: 15px;
  }
}
</style>

<style>
body.dark .billing-card__summary-item {
  color: #ffffff;
}

body.dark .billing-card__summary-item--success {
  background: rgba(6, 180, 138, 0.18);
}

body.dark .billing-card__summary-item--goal {
  background: rgba(131, 111, 255, 0.18);
}
</style>
